import { Box, styled } from '@mui/material';
import { SharedDocumentsIcon } from 'assets/img/icons';
import { DocumentsIcon } from 'assets/img/menu';
import DocumentSkeleton from 'FeatureModules/Document/Components/DocumentSkeleton';
import DocumentTable from 'FeatureModules/Document/Components/Tables/DocumentTable';
import { EDocumentsTableColumnKeys } from 'FeatureModules/Document/utils/documentHeaderColumns';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { EmptyStateCard, Tabs } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import usePlotDocuments from 'FeatureModules/Site/hooks/usePlotDocuments';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { EDocumentLibraryTabs } from 'FeatureModules/Document/types/document.types';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

const StyledInfiniteScrollWrapper = styled(InfiniteScrollWrapper)(({ theme }) => ({
  margin: theme.spacing(0, -2),
  padding: theme.spacing(0, 2),
  overflow: 'auto',
}));

const DOCUMENT_TABS: (ITab<EDocumentLibraryTabs> & {
  onlyForBuyer?: boolean;
})[] = [
  {
    key: EDocumentLibraryTabs.INTERNAL,
    label: 'Internal',
    startIcon: <DocumentsIcon height={12} />,
  },
  {
    key: EDocumentLibraryTabs.SHARED,
    label: 'Shared with me',
    startIcon: <SharedDocumentsIcon height={12} />,
    onlyForBuyer: true,
  },
];

const PlotsDocuments: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    documents,
    loading,
    error,
    searchTerm,
    filters,
    selectedTab,
    hasNextPage,
    handlePageEndReached,
    handleFilterChange,
    handleFilterReset,
    handleSearchTermChange,
    handleTabChange,
  } = usePlotDocuments();
  const { plan } = useCompanyPlanContext();

  const isFreemium = plan === ECustomerPlan.FREEMIUM;

  if (error) {
    return <ErrorState />;
  }
  if (loading) {
    return <Loader />;
  }

  if (!documents.length && !searchTerm && !filters.some(({ value }) => !!value)) {
    return (
      <PageContainer>
        <PageTitle
          title="Plot documents"
          goBackLabel="All plots"
          goBackUrl={`/sites/${id}/plots`}
        />

        {isFreemium ? (
          <Box mb={3} />
        ) : (
          <Box mt={1} mb={2}>
            <Tabs tabs={DOCUMENT_TABS} selectedTab={selectedTab} onChange={handleTabChange} />
          </Box>
        )}

        <EmptyStateCard
          title="Add documents to your plots to keep track of important information like proof of no deforestation"
          variant={EItemTypes.DOCUMENT}
          background={EBackgroundTypes.DETAILED_DOCUMENTS}
          buttonIcon={<Icon name="document-request-add" />}
          size={EAvailableSizes.LARGE}
          data-testid-button="add-new-document-btn"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle title="Plot documents" goBackLabel="All plots" goBackUrl={`/sites/${id}/plots`} />

      {isFreemium ? (
        <Box mb={3} />
      ) : (
        <Box mt={1} mb={2}>
          <Tabs tabs={DOCUMENT_TABS} selectedTab={selectedTab} onChange={handleTabChange} />
        </Box>
      )}

      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <Box position="relative">
        <ActionContainer>
          <SearchToolbar
            searchTerm={searchTerm}
            placeholder="Search document"
            className="search-input"
            setSearchTerm={handleSearchTermChange}
          />
        </ActionContainer>

        {!!documents.length && (
          <StyledInfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <ScrollableTableContainer>
              <DocumentTable
                multiselect={false}
                disableSelection
                columns={[
                  EDocumentsTableColumnKeys.TITLE,
                  EDocumentsTableColumnKeys.OWNER,
                  EDocumentsTableColumnKeys.USAGE,
                  EDocumentsTableColumnKeys.ISSUE_DATE,
                  EDocumentsTableColumnKeys.EXPIRY_DATE,
                ]}
                documents={documents}
                onDocumentClick={({ id }) =>
                  navigate(
                    `/documents/${
                      selectedTab === EDocumentLibraryTabs.INTERNAL ? 'internal' : 'shared'
                    }/${id}`
                  )
                }
              />
            </ScrollableTableContainer>
          </StyledInfiniteScrollWrapper>
        )}
      </Box>

      {!documents?.length && (
        <EmptySearchResults
          message="No search results found"
          skeletonComponent={<DocumentSkeleton title={searchTerm} />}
        />
      )}
    </PageContainer>
  );
};

export default PlotsDocuments;
